/**
 * The name a prototype pill shows, composed the way an artifact pill composes its own.
 *
 * `composeArtifactTitle` joins `artifact_name - artifact_type - version`, so an artifact reads
 * "StockPilot Tatmeen-First Pathway - ROI Assessment - V1". A prototype gets the same three
 * parts from its own equivalents: Buildo's product name, what the build produced (a prototype or
 * a wireframe -- `meta.mode` on the TaskFlowSpec, which is the closest thing a prototype has to
 * an artifact type), and the version index of the turn that built it.
 *
 * The name itself already exists and already reaches the browser: Buildo writes
 * `meta.project_name`, the AI API derives `prototypeTitle` from it per turn (falling back to the
 * stored `chef_project_links.title`), and the live `prototype_ready` event carries the same
 * string as `prototype_title`. The pill used to show `prototypeDisplayName` instead, which is
 * the build session's label written from the last user message -- so a turn the founder opened
 * with "yes" produced a prototype called "yes".
 */

/** What the build produced. `meta.mode` on the spec; a prototype unless it says otherwise. */
export type PrototypeKind = 'prototype' | 'wireframe';

export interface PrototypeTitleInput {
  /** Buildo's product name for this prototype (`prototypeTitle`, `prototype_title`). */
  prototypeTitle?: string | null;
  /**
   * The build session's label. Legacy rows hold a fragment of the conversation here, which is
   * why it is the last rung rather than the first.
   */
  prototypeDisplayName?: string | null;
  /** Which version of the prototype this turn built. Absent reads as the first. */
  prototypeVersionIndex?: number | null;
  /** From the turn's spec; only a live turn's pill lacks it, because the event omits mode. */
  prototypeKind?: PrototypeKind | null;
  /**
   * The turn has no finished version yet (building, or stopped before it passed). The number
   * is left off: a draft's index is not settled until it is verified, and an unfinished pill
   * read "V1" before the same turn finished as "V9".
   */
  unfinished?: boolean;
}

/** The class of object, on the pill's second line — the counterpart of the artifact's "Artifact". */
export const PROTOTYPE_KIND_LABEL = 'Prototype';

/** The middle segment of the title: what this build produced. */
const TYPE_LABELS: Record<PrototypeKind, string> = {
  prototype: 'Prototype',
  wireframe: 'Wireframe',
};

const cleaned = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * The name alone, without type or version. Returns `null` when the turn carries no name at all,
 * so a caller can tell "no name yet" from a composed title that happens to start with the type.
 */
export function resolvePrototypeName(input: PrototypeTitleInput): string | null {
  return cleaned(input.prototypeTitle) || cleaned(input.prototypeDisplayName) || null;
}

/**
 * The pill's title line: `{name} - {Prototype|Wireframe} - V{n}`.
 *
 * The version is present once the turn has a finished version, as it is on an artifact, where a
 * first version still reads "- V1"; a turn with no version index is the first. While building or
 * unfinished it is left off (`unfinished`). A turn with no name drops that segment
 * rather than inventing one, leaving "Prototype - V1".
 */
export function resolvePrototypeTitle(input: PrototypeTitleInput): string {
  const index = input.prototypeVersionIndex;
  const version =
    typeof index === 'number' && Number.isFinite(index) && index > 0 ? Math.floor(index) : 1;

  return [
    resolvePrototypeName(input),
    TYPE_LABELS[input.prototypeKind ?? 'prototype'] ?? TYPE_LABELS.prototype,
    input.unfinished ? null : `V${version}`,
  ]
    .filter(Boolean)
    .join(' - ');
}

/**
 * Read `meta.mode` off a turn's stored TaskFlowSpec (`generationText`, a JSON string on a
 * prototyper turn and null elsewhere).
 *
 * Only the mode is read and kept: the spec itself is a whole product definition, and putting one
 * on every message in the store to render three words would be paid for on every render.
 */
export function prototypeKindFromSpec(raw: unknown): PrototypeKind | null {
  if (!raw || typeof raw !== 'string') return null;
  const text = raw.trim();
  if (!text.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(text) as { meta?: { mode?: unknown } };
    const mode = parsed?.meta?.mode;
    return mode === 'wireframe' ? 'wireframe' : mode === 'prototype' ? 'prototype' : null;
  } catch {
    return null;
  }
}
